import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { authApi, VerifyOTPResponse } from '../api/auth.api';
import { signInWithGoogle } from '../lib/firebase';
import MarketingNav from '../components/MarketingNav';
import SiteFooter from '../components/SiteFooter';
import { LEGAL_PATHS } from '../legal/company';
import usePageMeta from '../hooks/usePageMeta';

/**
 * Where ?next= may send someone after they sign in. An allowlist, because taking
 * that value on trust would turn this page into an open redirect.
 */
const NEXT_DESTINATIONS = ['/create-website', '/dashboard'];

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Mobile or email — the two ways to receive a code. */
type Method = 'phone' | 'email';

/** The Google "G", in its published colours. */
const GoogleMark = () => (
  <svg className="w-[18px] h-[18px]" viewBox="0 0 48 48" aria-hidden focusable="false">
    <path
      fill="#4285F4"
      d="M45.12 24.5c0-1.56-.14-3.06-.4-4.5H24v8.51h11.84c-.51 2.75-2.06 5.08-4.39 6.64v5.52h7.11c4.16-3.83 6.56-9.47 6.56-16.17z"
    />
    <path
      fill="#34A853"
      d="M24 46c5.94 0 10.92-1.97 14.56-5.33l-7.11-5.52c-1.97 1.32-4.49 2.1-7.45 2.1-5.73 0-10.58-3.87-12.31-9.07H4.34v5.7C7.96 41.07 15.4 46 24 46z"
    />
    <path
      fill="#FBBC05"
      d="M11.69 28.18c-.44-1.32-.69-2.73-.69-4.18s.25-2.86.69-4.18v-5.7H4.34A21.99 21.99 0 0 0 2 24c0 3.55.85 6.91 2.34 9.88l7.35-5.7z"
    />
    <path
      fill="#EA4335"
      d="M24 10.75c3.23 0 6.13 1.11 8.41 3.29l6.31-6.31C34.91 4.18 29.93 2 24 2 15.4 2 7.96 6.93 4.34 14.12l7.35 5.7c1.73-5.2 6.58-9.07 12.31-9.07z"
    />
  </svg>
);

const LoginPage: React.FC = () => {
  const [method, setMethod] = useState<Method>('phone');
  const [step, setStep] = useState<'identifier' | 'otp' | 'name'>('identifier');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [name, setName] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [address, setAddress] = useState('');
  const [tempToken, setTempToken] = useState<string | null>(null);
  // Set when the address is already known and verified — an email code or a
  // Google sign-in — so registration does not ask for it again.
  const [emailOnFile, setEmailOnFile] = useState(false);
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [error, setError] = useState('');

  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();

  // Kept out of search: the page is a step on the way somewhere, and its ?next=
  // variants would otherwise compete with each other for the same query.
  usePageMeta({
    title: 'Login or Sign Up | Create Your Website with OnTheWeb',
    description: 'Log in to OnTheWeb to manage your website, update details and track your plan. New user? Sign up to create your business website in minutes.',
    path: '/login',
    index: false,
  });

  // Pages inside the app hand over the destination as router state. ontheweb.ai
  // links in from another origin, where only the URL can carry it.
  const nextParam = new URLSearchParams(location.search).get('next');
  const from =
    (location.state as any)?.from ||
    (nextParam && NEXT_DESTINATIONS.includes(nextParam) ? nextParam : '/dashboard');
  const savedPrompt = (location.state as any)?.prompt;
  // Set by callers that redirect here to gate a feature behind login (e.g.
  // clicking the voice-record button on the public landing page).
  const infoMessage = (location.state as any)?.message;

  const goToDestination = () =>
    navigate(from, { state: savedPrompt ? { prompt: savedPrompt } : undefined });

  /**
   * Both answers the server can give: a session, or a new account that still
   * needs a name and a business name.
   */
  const applySignIn = (response: VerifyOTPResponse, options: { emailOnFile: boolean }) => {
    if (response.isNewUser && response.tempToken) {
      setTempToken(response.tempToken);
      setEmailOnFile(options.emailOnFile);
      if (response.name) setName(response.name);
      setStep('name');
      return;
    }
    if (response.user && response.token && response.refreshToken) {
      login({ user: response.user, token: response.token, refreshToken: response.refreshToken });
      goToDestination();
    }
  };

  const handleSendCode = async (e?: React.FormEvent) => {
    e?.preventDefault();
    setError('');
    if (method === 'phone' && phone.length !== 10) {
      setError('Please enter a valid 10-digit phone number');
      return;
    }
    if (method === 'email' && !EMAIL_PATTERN.test(email.trim())) {
      setError('Please enter a valid email address');
      return;
    }
    setLoading(true);
    try {
      if (method === 'phone') {
        await authApi.sendOTP({ phone, countryCode: '+91' });
      } else {
        await authApi.sendEmailOTP({ email: email.trim() });
      }
      setStep('otp');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (otp.length !== 6) {
      setError('Please enter a valid 6-digit OTP');
      return;
    }
    setLoading(true);
    try {
      const response =
        method === 'phone'
          ? await authApi.verifyOTP({ phone, otp, countryCode: '+91' })
          : await authApi.verifyEmailOTP({ email: email.trim(), otp });
      applySignIn(response, { emailOnFile: method === 'email' });
    } catch (err: any) {
      setError(err.response?.data?.message || 'Invalid OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = async () => {
    setError('');
    setGoogleLoading(true);
    try {
      const idToken = await signInWithGoogle();
      // Null means the account chooser was closed. Nothing went wrong and there
      // is nothing to say about it.
      if (!idToken) return;
      const response = await authApi.googleAuth({ idToken });
      applySignIn(response, { emailOnFile: true });
    } catch (err: any) {
      setError(err.response?.data?.message || 'Could not sign in with Google. Please try again.');
    } finally {
      setGoogleLoading(false);
    }
  };

  const handleCompleteRegistration = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!name.trim()) {
      setError('Please enter your name');
      return;
    }
    if (!businessName.trim()) {
      setError('Please enter your business name');
      return;
    }
    if (!tempToken) {
      setError('Session expired. Please start again.');
      startOver();
      return;
    }
    setLoading(true);
    try {
      const authResponse = await authApi.completeRegistration({
        tempToken,
        name: name.trim(),
        businessName: businessName.trim(),
        email: emailOnFile ? undefined : contactEmail.trim() || undefined,
        address: address.trim() || undefined,
      });
      login(authResponse);
      goToDestination();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to complete registration');
    } finally {
      setLoading(false);
    }
  };

  const startOver = () => {
    setStep('identifier');
    setOtp('');
    setTempToken(null);
    setEmailOnFile(false);
    setError('');
    setName('');
    setBusinessName('');
    setContactEmail('');
    setAddress('');
  };

  const switchMethod = (next: Method) => {
    setMethod(next);
    setOtp('');
    setError('');
  };

  const sentTo = method === 'phone' ? `+91 ${phone}` : email.trim();

  // Column layout with a growing middle, so the footer sits at the bottom of the
  // viewport rather than directly under the short login card.
  return (
    <div className="nova-page relative overflow-hidden flex flex-col">
      <MarketingNav />
      <div className="hero-glow top-1/4 left-1/2 -translate-x-1/2" />
      <div className="relative z-10 flex-1 max-w-md w-full mx-auto px-4 pt-28 sm:pt-32 pb-16">
        <div className="text-center mb-8">
          <h1 className="font-display text-3xl font-extrabold text-nova-text">Login or Signup</h1>
          <p className="text-nova-text2 mt-2">
            New and returning users use this page. We will create an account if you are new.
          </p>
        </div>

        {infoMessage && (
          <div className="mb-6 p-3 bg-nova-sky/10 border border-nova-sky/30 text-nova-sky rounded-lg text-sm text-center">
            {infoMessage}
          </div>
        )}

        <div className="nova-card p-4 sm:p-8 shadow-nova-lg">
          {step === 'identifier' ? (
            <>
              <div className="flex gap-1 p-1 mb-6 bg-nova-bg2 border border-nova-border rounded-lg">
                {(['phone', 'email'] as Method[]).map((option) => (
                  <button
                    key={option}
                    type="button"
                    onClick={() => switchMethod(option)}
                    aria-pressed={method === option}
                    className={`flex-1 py-2 text-sm font-medium rounded-md transition-colors ${
                      method === option
                        ? 'bg-nova-bg4 text-nova-text'
                        : 'text-nova-text2 hover:text-nova-text'
                    }`}
                  >
                    {option === 'phone' ? 'Mobile number' : 'Email'}
                  </button>
                ))}
              </div>

              <form onSubmit={handleSendCode}>
                {method === 'phone' ? (
                  <div className="mb-6">
                    <label htmlFor="phone" className="nova-label">Phone Number</label>
                    <div className="flex">
                      <span className="inline-flex items-center px-3 rounded-l-lg border border-r-0 border-nova-border bg-nova-bg2 text-nova-text2 text-sm">
                        +91
                      </span>
                      <input
                        type="tel"
                        id="phone"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                        className="flex-1 px-4 py-3 bg-nova-bg2 border border-nova-border rounded-r-lg text-nova-text focus:outline-none focus:ring-2 focus:ring-nova-sky/30 focus:border-nova-sky"
                        placeholder="Enter your phone number"
                        required
                      />
                    </div>
                  </div>
                ) : (
                  <div className="mb-6">
                    <label htmlFor="email" className="nova-label">Email Address</label>
                    <input
                      type="email"
                      id="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="nova-input"
                      placeholder="you@example.com"
                      required
                    />
                    <p className="mt-2 text-xs text-nova-text3">
                      We will email you a one-time 6-digit code so you can sign in. We do not use
                      this address for marketing. A mobile number is not needed — you can add one
                      later from your profile.
                    </p>
                  </div>
                )}
                {error && <div className="nova-alert-error mb-4">{error}</div>}
                <button
                  type="submit"
                  disabled={loading || googleLoading}
                  className="btn-primary w-full justify-center py-3 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Sending OTP...' : 'Send OTP'}
                </button>
              </form>

              <div className="flex items-center gap-3 my-5">
                <span className="h-px flex-1 bg-nova-border" />
                <span className="text-xs uppercase tracking-wider text-nova-text3">or</span>
                <span className="h-px flex-1 bg-nova-border" />
              </div>
              <button
                type="button"
                onClick={handleGoogle}
                disabled={loading || googleLoading}
                className="btn-ghost w-full justify-center gap-2.5 py-3 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <GoogleMark />
                {googleLoading ? 'Opening Google...' : 'Continue with Google'}
              </button>
            </>
          ) : step === 'otp' ? (
            <form onSubmit={handleVerifyCode}>
              <div className="mb-6">
                <label htmlFor="otp" className="nova-label">Enter OTP</label>
                <p className="text-sm text-nova-text2 mb-3 flex flex-wrap items-center gap-x-2 gap-y-1">
                  <span>We've sent a 6-digit code to {sentTo}</span>
                  <button type="button" onClick={() => { setStep('identifier'); setOtp(''); setError(''); }} className="text-nova-sky hover:text-nova-sky2 font-medium">
                    Change
                  </button>
                </p>
                <input
                  type="text"
                  id="otp"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className="nova-input text-center text-2xl tracking-widest"
                  placeholder="000000"
                  required
                />
              </div>
              {error && <div className="nova-alert-error mb-4">{error}</div>}
              <button type="submit" disabled={loading} className="btn-primary w-full justify-center py-3 disabled:opacity-50">
                {loading ? 'Verifying...' : 'Verify OTP'}
              </button>
              <button type="button" onClick={() => handleSendCode()} disabled={loading} className="w-full mt-3 text-nova-sky hover:text-nova-sky2 py-2 text-sm font-medium">
                Resend OTP
              </button>
            </form>
          ) : (
            <form onSubmit={handleCompleteRegistration}>
              <p className="text-sm text-nova-text2 mb-5">Welcome! Tell us a bit about you and your business to get started.</p>
              <div className="mb-5">
                <label htmlFor="name" className="nova-label">Your Name</label>
                <input type="text" id="name" value={name} onChange={(e) => setName(e.target.value)} className="nova-input" placeholder="Enter your name" required />
              </div>
              <div className="mb-5">
                <label htmlFor="businessName" className="nova-label">Business Name</label>
                <input
                  type="text"
                  id="businessName"
                  value={businessName}
                  onChange={(e) => setBusinessName(e.target.value)}
                  className="nova-input"
                  placeholder="Enter your business name"
                  required
                />
              </div>
              {/* Asked for only when we have no address yet: someone who signed
                  in with an email code or with Google already gave us theirs. */}
              {!emailOnFile && (
                <div className="mb-5">
                  <label htmlFor="contactEmail" className="nova-label">
                    Email Address <span className="text-nova-text3 font-normal">(optional)</span>
                  </label>
                  <input
                    type="email"
                    id="contactEmail"
                    value={contactEmail}
                    onChange={(e) => setContactEmail(e.target.value)}
                    className="nova-input"
                    placeholder="you@example.com"
                  />
                </div>
              )}
              <div className="mb-6">
                <label htmlFor="address" className="nova-label">
                  Business Address <span className="text-nova-text3 font-normal">(optional)</span>
                </label>
                <textarea
                  id="address"
                  rows={2}
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  className="nova-input resize-none"
                  placeholder="Street, city, state, PIN code"
                />
              </div>
              {error && <div className="nova-alert-error mb-4">{error}</div>}
              <button type="submit" disabled={loading} className="btn-primary w-full justify-center py-3 disabled:opacity-50">
                {loading ? 'Completing...' : 'Continue to Dashboard'}
              </button>
              {/* Only shown on this step, which is reached exclusively by users
                  the server reported as new — an existing user logging in has
                  already accepted these. */}
              <p className="mt-4 text-xs text-nova-text3 text-center leading-relaxed">
                By signing up, you accept our{' '}
                <Link to={LEGAL_PATHS.terms} target="_blank" rel="noopener noreferrer" className="text-nova-sky hover:text-nova-sky2 font-medium">
                  Terms &amp; Conditions
                </Link>{' '}
                and{' '}
                <Link to={LEGAL_PATHS.privacy} target="_blank" rel="noopener noreferrer" className="text-nova-sky hover:text-nova-sky2 font-medium">
                  Privacy Policy
                </Link>
                .
              </p>
              <button
                type="button"
                onClick={startOver}
                className="w-full mt-3 text-nova-text2 hover:text-nova-text py-2 text-sm font-medium"
              >
                Start over
              </button>
            </form>
          )}
        </div>
      </div>

      {/* z-10 keeps the decorative glow, which is absolutely positioned, from
          painting over the footer links. */}
      <div className="relative z-10">
        <SiteFooter />
      </div>
    </div>
  );
};

export default LoginPage;
